import React, { useCallback, useState, useEffect } from 'react';
import {
  path,
  propOr,
  prop,
  pathOr,
  isEmpty,
  compose,
  keys,
  head,
  map,
  equals,
  sortBy,
  reverse,
  isNil,
  includes,
  find,
} from 'ramda';
import moment from 'moment';
import { useQuery, useMutation } from '@apollo/react-hooks';
import {
  GET_CANDIDATES,
  GET_JOBS,
  GET_TAGS,
  GET_CURRENT_SUBSCRIPTION,
  GET_SUBSCRIBERS,
  GET_HIRING_TEAM,
  GET_EMAIL_TEMPLATES,
} from '../apollo/queries';
import { UPDATE_CANDIDATE_STATUS, CREATE_CANDIDATE, DELETE_CANDIDATE, UPDATE_CANDIDATE_TAGS, EMAIL_BULK_CANDIDATES } from '../apollo/mutation';
import Footer from './Footer';
import {
  Page,
  Layout,
  Button,
  Select,
  ButtonGroup,
  Autocomplete,
  Card,
  Heading,
  TextStyle,
  Badge,
  Filters,
  Popover,
  ResourceList,
  EmptyState,
  AppProvider,
  Frame,
  Pagination,
  ResourceItem,
  Tabs,
  Checkbox,
  ChoiceList,
  Subheading,
  Modal,
  TextContainer,
  Spinner,
  Toast,
  Link,
  DropZone,
  Form,
  ActionList,
  FormLayout,
  Stack,
  Thumbnail,
  Caption,
  TextField,
  Tag,
} from '@shopify/polaris';
import { ExportMinor, CirclePlusMinor } from '@shopify/polaris-icons';
import { CSVLink } from 'react-csv';
import Loader from 'react-loader-spinner';
import { TitleBar } from '@shopify/app-bridge-react';
import { categories } from './JobSetup';
import { Editor } from '@tinymce/tinymce-react';

import { noJobsFoundImgUrl } from '../../utils/noJobsFoundImgUrl';

import { overallRatingButtons, statusBadgeColors, getOverallRatingKey } from './Candidate';

const appliedDateOptions = [
  { label: 'Last 7 days', value: '7' },
  { label: 'Last 14 days', value: '14' },
  { label: 'Last 30 days', value: '30' },
  { label: 'Last 90 days', value: '90' },
];

const isNilOrEmpty = (value) => isNil(value) || isEmpty(value);

const scoreOptions = [
  { label: '76 - 100', value: '75', test: ({ score = 0 }) => score > 75 && score <= 100 },
  { label: '51 - 75', value: '50', test: ({ score = 0 }) => score > 50 && score <= 75 },
  { label: '1 - 50', value: '1', test: ({ score = 0 }) => score > 0 && score <= 50 },
  { label: 'Knockout', value: '0', test: ({ score = 0, token = '' }) => !!token && score === 0 },
  { label: 'Sent', value: 'sent', test: ({ score, token = '' }) => !isNilOrEmpty(token) && isNilOrEmpty(score) },
];

const tabs = [
  {
    id: 'new_candidate',
    content: 'New',
    accessibilityLabel: 'New',
    panelID: 'new-candidates-content',
  },
  {
    id: 'active',
    content: 'Active',
    accessibilityLabel: 'Active',
    panelID: 'active-candidates-content',
  },
  {
    id: 'hired',
    content: 'Hired',
    accessibilityLabel: 'Hired',
    panelID: 'hired-candidates-content',
  },
  {
    id: 'inactive',
    content: 'Inactive',
    panelID: 'inactive-candidates-content',
  },
  {
    id: 'all',
    content: 'All',
    panelID: 'all-candidates-content',
  },
  {
    id: 'subscribers',
    content: 'Subscribers',
    panelID: 'subscribers-content',
  },
];

const ROWS_PER_PAGE = 15;

const CandidateList = (props) => {
  let { shopifyToken, match = {} } = props;
  const { params = {} } = { ...match };
  const { job: jobId } = { ...params };
  const [isLoading, setLoading] = useState(false);
  const [active, setActive] = useState(false);
  const [selected, setSelected] = useState(0);
  const [queryValue, setQueryValue] = useState(null);
  const [successToast, setSuccessToast] = useState(false);
  const [currentCandidate, setCurrentCandidate] = useState({});
  const [candidateDeletionModalState, setCandidateDeletionModalState] = useState({ open: false, candidateId: null });
  const [filterQueryCandidates, setFilterQueryCandidates] = useState(null);
  const [selectedJobsTitles, setSelectedJobsTitles] = useState(null);
  const [selectedRatings, setSelectedRatings] = useState(null);
  const [selectedTags, setSelectedTags] = useState(null);
  const [selectedDepartments, setSelectedDepartments] = useState(null);
  const [selectedLocations, setSelectedLocations] = useState(null);
  const [selectedScores, setSelectedScores] = useState(null);
  const [selectedDispositionCodes, setSelectedDispositionCodes] = useState(null);
  const [selectedAppliedDate, setSelectedAppliedDate] = useState(null);
  const [selectedTagsOperator, setSelectedTagsOperator] = useState('AND');
  const [locations, setLocations] = useState([]);
  const [jobFilters, setJobFilters] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentJob, setCurrentJob] = useState({});
  const [reason, setReason] = useState(null);
  const [sortValue, setSortValue] = useState('DATE_MODIFIED_DESC');
  const [updateCandidateStatus] = useMutation(UPDATE_CANDIDATE_STATUS);
  const [createCandidate] = useMutation(CREATE_CANDIDATE);
  const [deleteCandidate] = useMutation(DELETE_CANDIDATE);
  const [updateCandidateTags] = useMutation(UPDATE_CANDIDATE_TAGS);
  const [emailCandidateBulk] = useMutation(EMAIL_BULK_CANDIDATES);
  const handleModal = useCallback(() => setActive(!active), [active]);
  const [tabCandidates, setTabCandidates] = useState([]);
  const [isCandidateModalOpen, setIsCandidateModalOpen] = useState(false);
  const [bulkStatusModal, setBulkStatusModal] = useState(false);
  const [bulkCandidate, setBulkCandidate] = useState({});
  const [isTagModalOpen, setIsTagModalOpen] = useState('');
  const [isEmailModalOpen, setIsEmailModalOpen] = useState('');
  const [tagOptions, setTagOptions] = useState([]);
  const [popoverActive, setPopoverActive] = useState({});
  const [allTagOptions, setAllTagOptions] = useState([]);
  const [selectedBulkTags, setSelectedBulkTags] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [page, setPage] = useState(0);
  const [form, setForm] = useState({
    name: '',
    lastName: '',
    email: '',
    phone: '',
    jobId: '',
  });
  const [file, setFile] = useState();
  const toggleActive = useCallback(() => setSuccessToast((successToast) => !successToast), []);
  const toggleTagModal = () => setIsTagModalOpen(!isTagModalOpen);
  const toggleEmailModal = () => setIsEmailModalOpen(!isEmailModalOpen);
  const toggleIsCandidateModalOpen = () => setIsCandidateModalOpen(!isCandidateModalOpen);

  const handleQueryValueRemove = () => {
    setQueryValue(null);
    if (isNilOrEmpty(selectedJobsTitles)) return setFilterQueryCandidates(null);

    const valueLowerCase = selectedJobsTitles ? selectedJobsTitles.join(' ').toLowerCase() : '';
    setFilterQueryCandidates(tabCandidates.filter((item) => includes(item.jobTitle.toLowerCase(), valueLowerCase)));
  };

  const handleDropZoneDrop = useCallback((_dropFiles, acceptedFiles, _rejectedFiles) => setFile((file) => acceptedFiles[0]), []);

  const updateField = (name) => (value) => setForm({ ...form, [name]: value });

  const validImageTypes = ['application/pdf', 'image/jpeg', 'image/png'];

  const fileUpload = !file && <DropZone.FileUpload actionTitle='Add resume' actionHint='or drop an file to upload' />;
  const uploadedFile = file && (
    <Stack>
      <Thumbnail
        size='small'
        alt={file.name}
        source={
          validImageTypes.indexOf(file.type) > 0
            ? window.URL.createObjectURL(file)
            : 'https://cdn.shopify.com/s/files/1/0757/9955/files/New_Post.png?12678548500147524304'
        }
      />
      <div>
        {file.name} <Caption>{file.size} bytes</Caption>
      </div>
    </Stack>
  );
  const [emailForm, setEmailForm] = useState({});
  const [toastStatus, setToastStatus] = useState({ status: false, message: '' });

  const {
    loading: loadingTeam,
    data: dataTeam,
    refetch: refetchTeam,
  } = useQuery(GET_HIRING_TEAM, {
    variables: { shopifyToken: shopifyToken },
  });

  const {
    loading: loadingEmailTemplate,
    data: dataEmailTemplates,
    refetch: refetchEmailTemplate,
  } = useQuery(GET_EMAIL_TEMPLATES, {
    variables: { shopifyToken: shopifyToken },
  });

  const { loading, data, refetch } = useQuery(GET_CANDIDATES, {
    variables: {
      shopifyToken,
      status: tabs[selected]?.id === 'all' || tabs[selected]?.id === 'subscribers' ? undefined : tabs[selected]?.id,
    },
  });

  const { data: dataSubsc, refetch: refetchSubsc } = useQuery(GET_SUBSCRIBERS, {
    variables: { shopifyToken },
  });

  const { data: jobData } = useQuery(GET_JOBS, {
    variables: { shopifyToken, templates: false },
  });

  const { data: tagsData, refetch: tagsRefetch } = useQuery(GET_TAGS, {
    variables: { shopifyToken },
  });

  const { data: subscriptionData } = useQuery(GET_CURRENT_SUBSCRIPTION, { variables: { shopifyToken } });
  const shop = propOr({}, 'shop')(subscriptionData);
  const dispositionCodes = propOr([], 'dispositionCodes')(shop);

  const { currentSubscription = {} } = { ...shop };
  const isSubscribedUser = !isNilOrEmpty(currentSubscription);
  const isAllowedUser =
    isSubscribedUser &&
    (currentSubscription.subscriptionType === 'Premium' ||
      currentSubscription.subscriptionType === 'Growth' ||
      currentSubscription.subscriptionType === 'Business');

  const { careerPortal = {} } = { ...shop };
  const { departments = [] } = { ...careerPortal };

  const hiringTeams = propOr([], 'hiringTeam')(dataTeam);
  const emailTemplates = propOr([], 'emailTemplates')(dataEmailTemplates);
  const externalTemplates = (emailTemplates || []).filter(
    ({ group, status }) => (group === 'external' || group === 'user') && status === 'active'
  );

  const jobs = pathOr([], ['jobs', 'jobs'])(jobData);
  const tags = propOr([], 'tags')(tagsData);
  const tagsOptions = tags.map(({ name }) => ({
    label: name,
    value: name,
  }));

  const jobsOptions = jobs.reduce(
    (acc, job) => {
      if (job.status === 'open') {
        return [...acc, { label: job.title, value: job.id }];
      }
      return acc;
    },
    [{ value: '', label: 'Select a Job' }]
  );

  const emailTemplatesOptions = emailTemplates.reduce(
    (templatesArray, template) => {
      return [{ label: template.title, value: template.id }];
    },
    [{ value: '', label: 'Select an Email Template' }]
  );

  const candidates = propOr([], 'candidates')(data);
  const subscribers = propOr([], 'subscribers')(dataSubsc);
  const changeRoute = path(['history', 'push'])(props);

  const handleFiltersQueryChange = (value) => {
    setQueryValue(value);

    if (isNilOrEmpty(value) && isNilOrEmpty(selectedJobsTitles)) {
      return setFilterQueryCandidates(tabCandidates);
    }

    if (isNilOrEmpty(value) && !isNilOrEmpty(selectedJobsTitles)) {
      return setFilterQueryCandidates(filterQueryCandidates);
    }

    const valueLowerCase = selectedJobsTitles ? selectedJobsTitles.join(' ').toLowerCase() : '';

    setFilterQueryCandidates(
      tabCandidates.filter(function (item) {
        const searchedTextCondition = item.name.toLowerCase().search(value.toLowerCase()) !== -1;
        const bothConditions = searchedTextCondition && includes(item.jobTitle.toLowerCase(), valueLowerCase);

        return isNilOrEmpty(selectedJobsTitles) ? searchedTextCondition : bothConditions;
      })
    );
  };

  useEffect(() => {
    if (!isEmpty(tags) && isEmpty(allTagOptions)) {
      const options = tags.map(({ name }) => ({
        label: name,
        value: name,
      }));
      setTagOptions(options);
      setAllTagOptions(options);
    }
  }, [tags]);

  useEffect(() => {
    if (!!props.match.params.tab) {
      const tabIndex = tabs.findIndex(({ id }) => id === props.match.params.tab);
      if (tabIndex && tabIndex !== selected) {
        setSelected(tabIndex);
      }
    }
  }, [props.match.params.tab]);

  useEffect(() => {
    const tab = tabs[selected];

    if (!!tab) {
      if (tab.id === 'all') {
        if (candidates.length > 0) {
          setTabCandidates(candidates);
        } else if (tabCandidates.length !== 0) {
          setTabCandidates([]);
        }
      } else {
        if (candidates.length > 0) {
          setTabCandidates(candidates.filter(({ status }) => status === tab.id));
        } else if (tabCandidates.length !== 0) {
          setTabCandidates([]);
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates]);

  useEffect(() => {
    if (tabs[selected]?.id !== 'subscribers') refetch();
    setPage(0);
  }, [selected]);

  useEffect(() => {
    setPage(0);
  }, [filterQueryCandidates]);

  useEffect(() => {
    if (!!jobId && !isEmpty(tabCandidates)) {
      const jobCandidates = tabCandidates.filter((c) => c.job.id === jobId);
      if (!isEmpty(jobCandidates)) {
        const [job] = [...jobCandidates];
        handleChoiceListChange('job', [job.jobTitle]);
      }
    }
    if (!isNilOrEmpty(tabCandidates) && !!tabCandidates.map) {
      let locationsList = tabCandidates.map(({ job }) => {
        const { location, isRemote } = { ...job };
        return isRemote ? 'Remote' : location;
      });
      locationsList = locationsList.filter((item, index) => locationsList.indexOf(item) === index);
      setLocations(
        locationsList.map((item) => {
          return { label: item, value: item };
        })
      );

      let choicesItems = tabCandidates.map(({ jobTitle }) => jobTitle);
      choicesItems = choicesItems
        .filter((jobTitle, index) => choicesItems.indexOf(jobTitle) === index)
        .map((value) => ({ label: value, value }));
      setJobFilters(choicesItems);
    }
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabCandidates]);

  /*if (isEmpty(candidates) && loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100vh',
        }}>
        <Loader type='TailSpin' color='#6371c7' height={80} width={80} />
      </div>
    );
  }*/

  const handleCurrentCandidate = (id, targetStatus) => {
    const candidate = candidates.find((j) => j.id == id);
    if (candidate.status === 'new_candidate' && targetStatus === 'active') {
      updateStatus(candidate.id, targetStatus);
    } else {
      setCurrentJob(candidate.job);
      setCurrentCandidate({ ...candidate, targetStatus });
      setActive(!active);
    }
  };

  const handleBulkStatusModal = (targetStatus) => {
    setBulkCandidate({ targetStatus });
    setBulkStatusModal(!bulkStatusModal);
  };

  const handleTabChange = (selectedTabIndex) => {
    setFilterQueryCandidates(null);
    setSelectedLocations([]);
    setSelectedScores([]);
    setSelectedRatings([]);
    setSelectedTags([]);
    setSelectedDepartments([]);
    setSelectedJobsTitles([]);
    setSelectedDispositionCodes([]);
    setSelectedAppliedDate([]);
    setSelectedTagsOperator('AND');
    setSelected(selectedTabIndex);
    setPage(0);
  };

  const toastMarkup = successToast ? <Toast content='Candidate(s) updated successfully' onDismiss={toggleActive} /> : null;

  const updateStatus = (candidateId, newStatus) => {
    setLoading(true);
    const response = updateCandidateStatus({
      variables: {
        candidateId: candidateId || currentCandidate.id,
        shopifyToken: shopifyToken,
        status: newStatus || currentCandidate.targetStatus,
        reasonId: newStatus || currentCandidate.targetStatus === 'inactive' ? reason : null,
      },
    });

    setTimeout(() => {
      setLoading(false);
      setActive(false);
      setSuccessToast(true);
      refetch();
      refetchSubsc();
    }, 1500);

    const errors = pathOr([], ['data', 'updateStatus', 'errors'])(response);
    const emptyErrors = isEmpty(errors);
  };

  const bulkUpdateStatus = () => {
    setLoading(true);

    selectedRows.forEach((candidateId) => {
      updateCandidateStatus({
        variables: {
          candidateId: candidateId,
          shopifyToken: shopifyToken,
          status: bulkCandidate.targetStatus,
          reasonId: bulkCandidate.targetStatus === 'inactive' ? reason : null,
        },
      });
    });

    setTimeout(() => {
      setLoading(false);
      setBulkStatusModal(false);
      setSelectedRows([]);
      setSuccessToast(true);
      refetch();
      refetchSubsc();
    }, 1500);
  };

  const handleUpdateCandidatesTags = () => {
    setIsTagModalOpen(false);
    selectedRows.forEach((candidateId) => {
      updateCandidateTags({
        variables: { candidateId, shopifyToken, tags: selectedBulkTags },
      });
    });
    setTimeout(() => {
      setLoading(false);
      setSelectedRows([]);
      tagsRefetch();
      setSuccessToast(true);
      refetch();
      refetchSubsc();
    }, 1500);
  };

  const handleCandidateBulkEmail = () => {
    setIsEmailModalOpen(false);
    emailCandidateBulk({
      variables: {
        candidateIds: selectedRows,
        shopifyToken: shopifyToken,
        emailForm: emailForm,
      },
    })
      .then(bulkCandidateEmailSuccess)
      .catch(onFailure);
    setEmailForm({});
    setSelectedRows([]);
  };

  const bulkCandidateEmailSuccess = () => {
    setToastStatus({ status: true, message: 'Emails sent.' });
    setEmailForm({});
  };

  const onFailure = () => {
    setToastStatus({ status: true, message: 'There is an error with the server' });
  };

  const rows = filterQueryCandidates || tabCandidates;

  const ratingFilterItems = [
    ...overallRatingButtons.map(({ name }) => ({ label: name, value: getOverallRatingKey(name) })),
    { value: 'not-rated', label: 'Not Rated' },
  ];

  const handleChoiceListChange = (criteria, value) => {
    const valueLowerCase = value ? value.join(' ').toLowerCase() : '';
    const queryLowerCase = queryValue ? queryValue.toLowerCase() : '';
    if (criteria === 'tag') {
      setSelectedTags(value);
      if (!isNilOrEmpty(value)) {
        return setFilterQueryCandidates(
          tabCandidates.filter(({ tags: candidateTags = [] }) => {
            const tagNames = candidateTags.map(({ name }) => name);
            return value.reduce((acu, curr) => {
              if (selectedTagsOperator === 'AND') {
                return acu && tagNames.includes(curr);
              } else {
                return acu || tagNames.includes(curr);
              }
            }, selectedTagsOperator === 'AND');
          })
        );
      }
    }

    if (criteria === 'rating') {
      setSelectedRatings(value);
      if (!isNilOrEmpty(value)) {
        return setFilterQueryCandidates(
          tabCandidates.filter(({ candidateScore }) => {
            const { overallRating = 'not-rated' } = { ...candidateScore };
            return value.indexOf(overallRating) !== -1;
          })
        );
      }
    }

    if (criteria === 'location') {
      setSelectedLocations(value);
      if (!isNilOrEmpty(value)) {
        return setFilterQueryCandidates(
          tabCandidates.filter(({ job }) => {
            const { location, isRemote } = { ...job };
            return value.indexOf(isRemote ? 'Remote' : location) !== -1;
          })
        );
      }
    }

    if (criteria === 'disposition-code') {
      setSelectedDispositionCodes(value);
      if (!isNilOrEmpty(value)) {
        return setFilterQueryCandidates(
          tabCandidates.filter(({ dispositionCode }) => {
            const { id: codeId = '' } = { ...dispositionCode };
            return value.indexOf(codeId) !== -1;
          })
        );
      }
    }

    if (criteria === 'apply-date') {
      setSelectedAppliedDate(value);
      if (!isNilOrEmpty(value)) {
        return setFilterQueryCandidates(
          tabCandidates.filter(({ createdAt }) => {
            const [option = '90'] = [...value];
            return moment(createdAt).isBetween(moment(new Date()).subtract(Number(option), 'days'), moment(new Date()));
          })
        );
      }
    }

    if (criteria === 'job') {
      setSelectedJobsTitles(value);
    }

    if (criteria === 'department') {
      setSelectedDepartments(value);
      if (!isNilOrEmpty(value)) {
        return setFilterQueryCandidates(
          tabCandidates.filter((el) => {
            const { job = {} } = { ...el };
            const { department = {} } = { ...job };
            const { id: departmentId = '' } = { ...department };
            return value.indexOf(departmentId) !== -1;
          })
        );
      }
    }

    if (criteria === 'score') {
      setSelectedScores(value);
      if (!isNilOrEmpty(value)) {
        return setFilterQueryCandidates(
          tabCandidates.filter((el) => {
            const { candidateQuestionnaire = {} } = { ...el };
            const scoresFilter = value
              .map((fel) => scoreOptions.find(({ value }) => value === fel) || {})
              .map(({ test }) => test({ ...candidateQuestionnaire }));
            return scoresFilter.reduce((a, b) => a || b, false);
          })
        );
      }
    }

    if (isNilOrEmpty(value) && isNilOrEmpty(queryValue)) {
      return setFilterQueryCandidates(tabCandidates);
    }

    if (!isNilOrEmpty(value) && isNilOrEmpty(queryValue)) {
      return setFilterQueryCandidates(tabCandidates.filter(({ jobTitle }) => includes(jobTitle.toLowerCase(), valueLowerCase)));
    }

    if (isNilOrEmpty(value) && !isNilOrEmpty(queryValue)) {
      return setFilterQueryCandidates(tabCandidates.filter(({ name }) => includes(queryLowerCase, name.toLowerCase())));
    }

    setFilterQueryCandidates(
      tabCandidates.filter(({ jobTitle, name }) => {
        return includes(queryLowerCase, name.toLowerCase()) && includes(jobTitle.toLowerCase(), valueLowerCase);
      })
    );
  };

  const filters = [
    {
      key: 'job',
      label: 'Job',
      filter: (
        <ChoiceList
          title='Job'
          titleHidden
          choices={jobFilters}
          selected={selectedJobsTitles || []}
          onChange={(value) => handleChoiceListChange('job', value)}
          allowMultiple
        />
      ),
      shortcut: true,
    },
    {
      key: 'rating',
      label: 'Rating',
      filter: (
        <ChoiceList
          title='Rating'
          titleHidden
          selected={selectedRatings || []}
          choices={ratingFilterItems}
          onChange={(value) => handleChoiceListChange('rating', value)}
          allowMultiple
          key={'rating'}
        />
      ),
      shortcut: true,
    },
    {
      key: 'hiring-location',
      label: 'Hiring location',
      filter: (
        <ChoiceList
          title='Hiring location'
          titleHidden
          selected={selectedLocations || []}
          choices={locations}
          onChange={(value) => handleChoiceListChange('location', value)}
          allowMultiple
        />
      ),
      shortcut: true,
    },
    isAllowedUser
      ? {
          key: 'score',
          label: 'Score',
          filter: (
            <ChoiceList
              title='Score'
              titleHidden
              selected={selectedScores || []}
              choices={scoreOptions}
              onChange={(value) => handleChoiceListChange('score', value)}
              allowMultiple
            />
          ),
          shortcut: true,
        }
      : null,
    {
      key: 'department',
      label: 'Department',
      filter: (
        <ChoiceList
          title='Department'
          titleHidden
          selected={selectedDepartments || []}
          choices={departments.map(({ id: value, headingText: label }) => ({ value, label }))}
          onChange={(value) => handleChoiceListChange('department', value)}
          allowMultiple
        />
      ),
      shortcut: false,
    },
    !isEmpty(tagsOptions)
      ? {
          key: 'tag',
          label: 'Tags',
          filter: (
            <>
              {/*<p>Resolve with</p>
              <ChoiceList
                title='Resolve with'
                titleHidden
                selected={selectedTagsOperator}
                choices={[
                  { label: 'AND', value: 'AND' },
                  { label: 'OR', value: 'OR' },
                ]}
                onChange={(value) => {
                  setSelectedTagsOperator(value.join(''));
                  handleChoiceListChange('tag', selectedTags);
                }}
              />
              <br />*/}
              <ChoiceList
                title='Tags'
                titleHidden
                selected={selectedTags || []}
                choices={tagsOptions}
                onChange={(value) => handleChoiceListChange('tag', value)}
                allowMultiple
                key={'tag'}
              />
            </>
          ),
          shortcut: true,
        }
      : null,
    {
      key: 'apply-date',
      label: 'Apply date',
      filter: (
        <ChoiceList
          title='Apply date'
          titleHidden
          selected={selectedAppliedDate || []}
          choices={appliedDateOptions}
          onChange={(value) => handleChoiceListChange('apply-date', value)}
        />
      ),
      shortcut: false,
    },
    {
      key: 'disposition-code',
      label: 'Disposition codes',
      filter: (
        <ChoiceList
          title='Disposition codes'
          titleHidden
          selected={selectedDispositionCodes || []}
          choices={dispositionCodes.map(({ id: value, name: label }) => ({ value, label }))}
          onChange={(value) => handleChoiceListChange('disposition-code', value)}
          allowMultiple
          disabled={selected < 3}
        />
      ),
      shortcut: false,
      disabled: selected < 3,
    },
  ].filter((el) => !!el);

  const handleJobsTitleFilterRemove = () => {
    setSelectedJobsTitles([]);

    if (queryValue) {
      return setFilterQueryCandidates(
        tabCandidates.filter((item) => item.jobTitle.toLowerCase().search(queryValue.toLowerCase()) !== -1)
      );
    }
    setFilterQueryCandidates(tabCandidates);
  };

  const handleLocationFilterRemove = () => {
    setSelectedLocations([]);
    setFilterQueryCandidates(tabCandidates);
  };

  const handleScoreFilterRemove = () => {
    setSelectedScores([]);
    setFilterQueryCandidates(tabCandidates);
  };

  const handleRatingFilterRemove = () => {
    setSelectedRatings([]);
    setFilterQueryCandidates(tabCandidates);
  };

  const handleDispositionCodeFilterRemove = () => {
    setSelectedDispositionCodes([]);
    setFilterQueryCandidates(tabCandidates);
  };

  const handleAppliedDateFilterRemove = () => {
    setSelectedAppliedDate([]);
    setFilterQueryCandidates(tabCandidates);
  };

  const handleClearAllFilters = () => {
    setSelectedLocations([]);
    setSelectedRatings([]);
    setSelectedScores([]);
    setSelectedTags([]);
    setSelectedDispositionCodes([]);
    setSelectedJobsTitles([]);
    setSelectedAppliedDate([]);
    setSelectedDepartments([]);
    setFilterQueryCandidates(tabCandidates);
  };

  const handleTagFilterRemove = () => {
    setSelectedTags([]);
    setFilterQueryCandidates(tabCandidates);
  };

  const handleDepartmentsFilterRemove = () => {
    setSelectedDepartments([]);
    setFilterQueryCandidates(tabCandidates);
  };

  const appliedFilters = [];

  if (!isNilOrEmpty(selectedJobsTitles)) {
    const key = 'job';
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, selectedJobsTitles),
      onRemove: handleJobsTitleFilterRemove,
    });
  }

  if (!isNilOrEmpty(selectedScores)) {
    const key = 'score';
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, selectedScores),
      onRemove: handleScoreFilterRemove,
    });
  }

  if (!isNilOrEmpty(selectedLocations)) {
    const key = 'location';
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, selectedLocations),
      onRemove: handleLocationFilterRemove,
    });
  }

  if (!isNilOrEmpty(selectedRatings)) {
    const key = 'rating';
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, selectedRatings),
      onRemove: handleRatingFilterRemove,
    });
  }

  if (!isNilOrEmpty(selectedDispositionCodes)) {
    const key = 'disposition-code';
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, selectedDispositionCodes),
      onRemove: handleDispositionCodeFilterRemove,
    });
  }

  if (!isNilOrEmpty(selectedAppliedDate)) {
    const key = 'apply-date';
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, selectedAppliedDate),
      onRemove: handleAppliedDateFilterRemove,
    });
  }

  if (!isNilOrEmpty(selectedTags)) {
    const key = 'tag';
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, selectedTags),
      onRemove: handleTagFilterRemove,
    });
  }

  if (!isNilOrEmpty(selectedDepartments)) {
    const key = 'department';
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, selectedDepartments),
      onRemove: handleDepartmentsFilterRemove,
    });
  }

  const modalMarkup = isLoading ? (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Loader type='TailSpin' color='#6371c7' height={80} width={80} />
    </div>
  ) : (
    <>
      {currentCandidate.targetStatus === 'inactive' && (
        <>
          <p>{`Why are you disqualifying ${currentCandidate.name}${
            !!currentCandidate.lastName ? ` ${currentCandidate.lastName}` : ''
          } from the ${currentCandidate.jobTitle} position?`}</p>
          <br />
          <Select
            labelHidden={true}
            options={dispositionCodes.map(({ id: value, name: label }) => ({ value, label }))}
            onChange={setReason}
            placeholder={'Select a disqualification reason'}
            value={reason}
          />
        </>
      )}
      {currentCandidate.targetStatus !== 'inactive' && (
        <TextContainer>
          <Heading>{`Are you sure you want to move this candidate to ${currentCandidate.targetStatus}?`}</Heading>
          <p>
            {' '}
            Candidate: {currentCandidate.name}
            {!!currentCandidate.lastName ? ` ${currentCandidate.lastName}` : ''}{' '}
          </p>
          <p> Job Title: {currentCandidate.jobTitle} </p>
          <p>
            {' '}
            Category:{' '}
            {compose(
              propOr(currentJob.category, 'label'),
              find((c) => c.value == currentJob.category)
            )(categories)}{' '}
          </p>
          <p> Location: {currentJob.isRemote ? 'Remote' : currentJob.location} </p>
        </TextContainer>
      )}
    </>
  );

  const bulkModalMarkup = isLoading ? (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Loader type='TailSpin' color='#6371c7' height={80} width={80} />
    </div>
  ) : (
    <>
      {bulkCandidate.targetStatus === 'inactive' && (
        <>
          <p>
            Disqualifying these candidates will move them to an inactive status and send them an email notifying them that they
            are no longer being considered for the role. Please select the reason why you're Disqualifying them.
          </p>
          <br />
          <Select
            labelHidden={true}
            options={dispositionCodes.map(({ id: value, name: label }) => ({ value, label }))}
            onChange={setReason}
            placeholder={'Select a disqualification reason'}
            value={reason}
          />
        </>
      )}
      {bulkCandidate.targetStatus !== 'inactive' && (
        <p>{`These candidates will now be displayed in the ${bulkCandidate.targetStatus} tab on the candidates and job details pages.`}</p>
      )}
    </>
  );

  const csvBody = compose(
    map(({ jobTitle, name, lastName, email, phone, resume, createdAt }) => ({
      jobTitle,
      name,
      lastName,
      email,
      phone,
      resume,
      createdAt,
    }))
  )(rows || []);

  const csvHeaders = compose(
    map((key) => {
      const getLabel = () => {
        switch (key) {
          case 'jobTitle':
            return 'Job';
          case 'name':
            return 'First name';
          case 'lastName':
            return 'Last name';
          case 'email':
            return 'Email';
          case 'phone':
            return 'Phone';
          case 'resume':
            return 'Resume(Link)';
          case 'createdAt':
            return 'Applied date';
        }
      };

      return { label: getLabel(), key };
    }),
    keys,
    head
  )(csvBody);

  const listResourceName = { singular: 'candidate', plural: 'candidates' };

  const sortListOptions = [
    { label: 'Recently updated', value: 'RECENTLY_UPDATED' },
    { label: 'Newest', value: 'RECENTLY_APPLIED_DESC' },
    { label: 'Oldest', value: 'RECENTLY_APPLIED_ASC' },
    { label: 'Name A - Z', value: 'NAME_ASC' },
    { label: 'Name Z - A', value: 'NAME_DES' },
    { label: 'Job title A - Z', value: 'TITLE_ASC' },
    { label: 'Job title Z - A', value: 'TITLE_DES' },
    isAllowedUser ? { label: 'Score', value: 'SCORE_DES' } : null,
  ].filter((el) => !!el);

  const onSortChangeList = (selected) => {
    setSortValue(selected);

    if (equals(selected, 'RECENTLY_UPDATED')) {
      compose(setFilterQueryCandidates, reverse, sortBy(prop('updatedAt')))(rows);
    }

    if (equals(selected, 'RECENTLY_APPLIED_DESC')) {
      compose(setFilterQueryCandidates, reverse, sortBy(prop('createdAt')))(rows);
    }

    if (equals(selected, 'RECENTLY_APPLIED_ASC')) {
      compose(setFilterQueryCandidates, sortBy(prop('createdAt')))(rows);
    }

    if (equals(selected, 'TITLE_ASC')) {
      compose(setFilterQueryCandidates, sortBy(prop('jobTitle')))(rows);
    }

    if (equals(selected, 'TITLE_DES')) {
      compose(setFilterQueryCandidates, reverse, sortBy(prop('jobTitle')))(rows);
    }

    if (equals(selected, 'NAME_ASC')) {
      compose(setFilterQueryCandidates, sortBy(prop('name')))(rows);
    }

    if (equals(selected, 'NAME_DES')) {
      compose(setFilterQueryCandidates, reverse, sortBy(prop('name')))(rows);
    }

    if (equals(selected, 'SCORE_DES')) {
      const scoredRows = rows.map((el) => {
        const { candidateQuestionnaire = {} } = { ...el };
        return { ...el, ['candidate_questionnaire_score']: candidateQuestionnaire?.score || 0 };
      });
      compose(setFilterQueryCandidates, reverse, sortBy(prop('candidate_questionnaire_score')))(scoredRows);
    }
  };

  const promotedBulkActions = [
    {
      content: 'Add tags',
      onAction: toggleTagModal,
    },
    {
      content: 'Send email',
      onAction: toggleEmailModal,
    },
  ];

  const bulkActions = [
    {
      content: 'Move to Active',
      onAction: () => handleBulkStatusModal('active'),
    },
    {
      content: 'Move to Hired',
      onAction: () => handleBulkStatusModal('hired'),
    },
    {
      content: 'Disqualify',
      onAction: () => handleBulkStatusModal('inactive'),
    },
  ];

  const removeTag = (tag) => () => {
    const options = [...selectedBulkTags];
    options.splice(options.indexOf(tag), 1);
    setSelectedBulkTags(options);
  };

  const handleNewTag = () => {
    const newOption = { label: inputValue, value: inputValue };
    setSelectedBulkTags([...selectedBulkTags, inputValue]);
    setAllTagOptions([...allTagOptions, newOption]);
    setTagOptions([...allTagOptions, newOption]);
    setInputValue('');
  };

  const toCandidatesTextLabel = () => {
    if (selectedRows.length > 0) {
      const label = candidates.filter((candidate) => candidate.id == selectedRows[0]);
      if (label.length > 0) {
        return label[0].email + ', ' + (selectedRows.length - 1) + ' more';
      }
    }
  };

  const updateText = (value) => {
    setInputValue(value);

    if (value === '') {
      setTagOptions(allTagOptions);
      return;
    }

    const filterRegex = new RegExp(value, 'i');
    const resultOptions = allTagOptions.filter((option) => option.label.match(filterRegex));
    setTagOptions(resultOptions);
  };

  const tagsMarkup = selectedBulkTags.map((option) => {
    return (
      <Tag key={`option${option}`} onRemove={removeTag(option)}>
        {option}
      </Tag>
    );
  });

  const statusName = (status) => {
    if ('new_candidate' === status) return 'New';
    if ('inactive' === status) return 'Disqualified';
    return `${status}`.charAt(0).toUpperCase() + `${status}`.slice(1);
  };

  const onChangeEmailTemplate = (template) => {
    setEmailForm({ ...emailForm, template });
    const selected = externalTemplates.find(({ id }) => id === template);
    if (!!selected) {
      const { body: message, subject, id } = { ...selected };
      setEmailForm({ ...emailForm, subject, message, id });
    }
  };

  const onRenderListItems = (item) => {
    const { id, name, jobTitle, phone, createdAt, status, lastName, candidateScore, candidateQuestionnaire } = item;
    const { overallRating = '' } = { ...candidateScore };
    return (
      <ResourceList.Item id={id} accessibilityLabel={`View details for ${name}`}>
        <div className='d-sm-none candidate-list-item'>
          <td
            style={{
              width: '20%',
            }}
            onClick={() => changeRoute(`/candidate/${id}`)}>
            <h3>
              <Link onClick={() => changeRoute(`/candidate/${id}`)} variation='strong' monochrome removeUnderline>
                <TextStyle variation='strong'>{name}</TextStyle>
                <TextStyle variation='strong'> {lastName}</TextStyle>
              </Link>
            </h3>
            <span style={{ fontWeight: '300', fontSize: '12px' }}>Applied {moment(createdAt).format('MMMM D, YYYY')}</span>
            {/* <div>{email}</div>
            <div>{phone}</div> */}
            <div className={'mobile-content'} style={{ position: 'absolute', top: '3vw', right: '1vh' }}>
              {status !== 'hired' ? (
                <Badge status={statusBadgeColors[status]}>
                  <span className='capitalize'>{status === 'new_candidate' ? 'New' : status}</span>
                </Badge>
              ) : (
                <span
                  style={{
                    backgroundColor: '#5EEEEB',
                    border: '0.2rem solid #fff',
                    color: '#454f5b',
                    padding: '.2rem .8rem',
                    borderRadius: '25px',
                    textAlign: 'center',
                    fontSize: '1.3rem',
                    lineHeight: '2rem',
                  }}>
                  Hired
                </span>
              )}
            </div>
          </td>
          <td style={{ width: '20%' }} onClick={() => changeRoute(`/candidate/${id}`)}>
            {jobTitle}
          </td>
          <td className='capitalize desktop-content' style={{ width: '10%', minWidth: '70px', textAlign: 'center' }} k>
            <Popover
              active={popoverActive[id]}
              activator={
                <Button
                  disclosure
                  plain
                  monochrome
                  onClick={() =>
                    setPopoverActive({
                      [id]: true,
                    })
                  }>
                  {status !== 'hired' ? (
                    <Badge status={statusBadgeColors[status]}>
                      <span className='capitalize'>{status === 'new_candidate' ? 'New' : status}</span>
                    </Badge>
                  ) : (
                    <span
                      style={{
                        backgroundColor: '#5EEEEB',
                        border: '0.2rem solid #fff',
                        color: '#454f5b',
                        padding: '.2rem .8rem',
                        borderRadius: '25px',
                        textAlign: 'center',
                        fontSize: '1.3rem',
                        lineHeight: '2rem',
                      }}>
                      Hired
                    </span>
                  )}
                </Button>
              }
              onClose={() =>
                setPopoverActive({
                  [id]: false,
                })
              }>
              <ActionList
                items={[
                  {
                    content: 'Active',
                    onAction: () => status !== 'active' && handleCurrentCandidate(id, 'active'),
                    active: status === 'active',
                  },
                  {
                    content: 'Hired',
                    onAction: () => status !== 'hired' && handleCurrentCandidate(id, 'hired'),
                    active: status === 'hired',
                  },
                  {
                    content: 'Disqualify',
                    onAction: () => status !== 'inactive' && handleCurrentCandidate(id, 'inactive'),
                    active: status === 'inactive',
                  },
                ]}
              />
            </Popover>
          </td>
          {isAllowedUser && (
            <td
              className='capitalize rating-col'
              style={{ width: '15%', minWidth: '70px', textAlign: 'center' }}
              onClick={() => changeRoute(`/candidate/${id}`)}>
              <span
                style={
                  !isNilOrEmpty(candidateQuestionnaire?.score) && candidateQuestionnaire?.score === 0 ? { color: '#d92b0c' } : {}
                }>
                {!isNilOrEmpty(candidateQuestionnaire?.score)
                  ? `${candidateQuestionnaire.score}%`
                  : !!candidateQuestionnaire?.token
                  ? 'Sent'
                  : ''}
              </span>
            </td>
          )}
          <td
            className='capitalize rating-col'
            style={{ width: '10%', minWidth: '70px', textAlign: 'center' }}
            onClick={() => changeRoute(`/candidate/${id}`)}>
            {!!overallRating && (
              <span className='capitalize'>
                {{ ...overallRatingButtons.find(({ name }) => overallRating === getOverallRatingKey(name)) }.name}
              </span>
            )}
          </td>
          <td style={{ width: '25%', display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center' }}>
            <ButtonGroup segmented>
              <Button size='slim' onClick={() => changeRoute(`/candidate/${id}`)}>
                Details
              </Button>
              {item.resume === '' ? (
                <Button disabled size='slim'>
                  {' '}
                  Resume{' '}
                </Button>
              ) : (
                <Button size='slim'>
                  <a href={item.resume} target='_blank'>
                    Resume
                  </a>
                </Button>
              )}
                <Button size='slim' onClick={() => setCandidateDeletionModalState({ open: true, candidateId: item.id })}>
                  Delete
                </Button>
            </ButtonGroup>
          </td>
        </div>
      </ResourceList.Item>
    );
  };

  const onRenderSubscriberItems = (item) => {
    const { id, name, lastName, interests, phone, createdAt, status, email, visitorData, resume } = { ...item };
    const { device } = { ...visitorData };
    return (
      <ResourceList.Item id={id} accessibilityLabel={`View details for ${name}`}>
        <div className='d-sm-none candidate-list-item'>
          <td
            style={{
              width: '20%',
            }}>
            <h3>
              <TextStyle variation='strong'>
                {name} {lastName}
              </TextStyle>
            </h3>
            <span style={{ fontWeight: '300', fontSize: '12px' }}>Subscribed: {moment(createdAt).format('MMMM D, YYYY')}</span>
            <div className={'mobile-content'} style={{ position: 'absolute', top: '3vw', right: '1vh' }}>
              <Badge status={status !== 'active' ? 'new' : 'success'}>{status}</Badge>
            </div>
          </td>
          <td style={{ width: '20%' }}>
            <div>{email}</div>
            <span style={{ fontWeight: '300', fontSize: '12px' }}>Phone: {phone}</span>
          </td>
          <td style={{ width: '15%' }}>
            <div>
              {interests === 'all' ? 'All categories' : (categories.find(({ value }) => value === interests) || {}).label}
            </div>
          </td>
          <td style={{ width: '15%', textTransform: 'capitalize' }}>
            <div>{device}</div>
          </td>
          <td className='capitalize desktop-content' style={{ width: '15%', minWidth: '70px', textAlign: 'center' }}>
            <Badge status={status !== 'active' ? 'new' : 'success'}>{status}</Badge>
          </td>
          <td>
            <Button size={'slim'} url={resume} external={true} disabled={!resume}>
              View resume
            </Button>
          </td>
        </div>
      </ResourceList.Item>
    );
  };

  const rowsLength = rows.length;
  const counterListText =
    rowsLength === 0
      ? ''
      : `Showing ${rowsLength} ${equals(rowsLength, 1) ? listResourceName.singular : listResourceName.plural}`;

  const emptyStateMarkup =
    rowsLength === 0 ? (
      <EmptyState heading='No candidates found' image={noJobsFoundImgUrl}>
        <p>Try changing the filters or search term</p>
      </EmptyState>
    ) : undefined;

  return (
    <AppProvider
      i18n={{
        Polaris: {
          Filters: {
            clear: 'Clear',
            moreFilters: 'More filters',
            clearAllFilters: 'Clear all filters',
            done: 'Done',
          },
          ResourceList: {
            selected: '{selectedItemsCount} selected',
            BulkActions: {
              moreActionsActivatorLabel: 'Update status',
            },
          },
        },
      }}>
      <Frame>
        <TitleBar title='Candidates' />
        <Page
          title='Candidates'
          fullWidth
          subtitle={
            !isEmpty(csvBody) && (
              <Button plain monochrome outline={false} icon={ExportMinor}>
                <CSVLink filename='candidates.csv' data={csvBody} headers={csvHeaders}>
                  Export
                </CSVLink>
              </Button>
            )
          }
          primaryAction={{
            content: 'Add candidate',
            onAction: () => toggleIsCandidateModalOpen(),
          }}>
          <Card>
            <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange}>
              <Card.Section>
                <div className='position-relative'>
                  {isEmpty(selectedRows) && selected !== 5 && (
                    <span
                      className={`d-sm-none resource-job-list__bulk_actions ${
                        isNilOrEmpty(selectedJobsTitles) &&
                        isNilOrEmpty(selectedLocations) &&
                        isNilOrEmpty(selectedScores) &&
                        isNilOrEmpty(selectedRatings) &&
                        isNilOrEmpty(selectedTags) &&
                        isNilOrEmpty(selectedDepartments) &&
                        isNilOrEmpty(selectedDispositionCodes) &&
                        isNilOrEmpty(selectedAppliedDate)
                          ? ''
                          : 'resource-job-list__counter--large'
                      }`}>
                      {counterListText}
                    </span>
                  )}
                  <ResourceList
                    resourceName={listResourceName}
                    filterControl={
                      selected !== 5 ? (
                        <Filters
                          queryValue={queryValue}
                          filters={filters}
                          appliedFilters={appliedFilters}
                          onQueryChange={handleFiltersQueryChange}
                          onQueryClear={handleQueryValueRemove}
                          onClearAll={handleClearAllFilters}
                        />
                      ) : null
                    }
                    items={(selected !== 5 ? rows : subscribers).filter((el, index) => {
                      const init = page * ROWS_PER_PAGE;
                      const end = init + ROWS_PER_PAGE;
                      return index >= init && index < end;
                    })}
                    emptyState={emptyStateMarkup}
                    sortValue={sortValue}
                    hasMoreItems={true}
                    showHeader={selected !== 5}
                    sortOptions={selected !== 5 ? sortListOptions : null}
                    onSortChange={onSortChangeList}
                    renderItem={selected !== 5 ? onRenderListItems : onRenderSubscriberItems}
                    totalItemsCount={50}
                    selectedItems={selectedRows}
                    onSelectionChange={setSelectedRows}
                    promotedBulkActions={selected !== 5 ? promotedBulkActions : null}
                    bulkActions={selected !== 5 ? bulkActions : null}
                    loading={loading}
                  />
                </div>
              </Card.Section>
              {Math.ceil((selected !== 5 ? rowsLength : (subscribers || []).length) / ROWS_PER_PAGE) > 0 && (
                <Card.Section>
                  <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', marginRight: '1.2rem' }}>
                    <Pagination
                      hasPrevious={page > 0}
                      onPrevious={() => {
                        setPage(page - 1);
                      }}
                      label={`Page ${page + 1} of ${Math.ceil(
                        (selected !== 5 ? rowsLength : (subscribers || []).length) / ROWS_PER_PAGE
                      )}`}
                      hasNext={page * ROWS_PER_PAGE + ROWS_PER_PAGE < (selected !== 5 ? rowsLength : (subscribers || []).length)}
                      onNext={() => {
                        setPage(page + 1);
                      }}
                    />
                  </div>
                </Card.Section>
              )}
            </Tabs>
            {toastMarkup}
          </Card>
        </Page>
        <Footer link='https://help.shophire.co/article/5-candidates' name='Candidates' />
        <Modal
          open={active}
          onClose={handleModal}
          title={currentCandidate.targetStatus === 'inactive' ? 'Disqualification reason' : null}
          primaryAction={{
            content: currentCandidate.targetStatus === 'inactive' ? 'Save' : 'Yes',
            onAction: () => updateStatus(),
            disabled: isLoading || (currentCandidate.targetStatus === 'inactive' && !reason),
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: handleModal,
              disabled: isLoading,
            },
          ]}
          loading={false}>
          <Modal.Section>{modalMarkup}</Modal.Section>
        </Modal>
        <Modal
          open={bulkStatusModal}
          onClose={handleBulkStatusModal}
          title={
            bulkCandidate.targetStatus === 'inactive'
              ? `Disqualify ${selectedRows.length} candidate(s)`
              : `Move ${selectedRows.length} candidate(s) to ${bulkCandidate.targetStatus}`
          }
          primaryAction={{
            content: 'Save',
            onAction: () => bulkUpdateStatus(),
            disabled: isLoading || (bulkCandidate.targetStatus === 'inactive' && !reason),
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: handleBulkStatusModal,
              disabled: isLoading,
            },
          ]}
          loading={false}>
          <Modal.Section>{bulkModalMarkup}</Modal.Section>
        </Modal>
        <Modal
          open={candidateDeletionModalState.open}
          onClose={() => setCandidateDeletionModalState({ open: false, candidateId: null})}
          title={'Delete Candidate'}
          primaryAction={{
            destructive: true,
            content: 'Delete candidate',
            onAction: async () => {
              if (candidateDeletionModalState.candidateId !== null) {
                setLoading(true);
                await deleteCandidate({ variables: {
                  shopifyToken,
                  candidateId: candidateDeletionModalState.candidateId
                }});
                setLoading(false);
                refetch();
                setCandidateDeletionModalState({ open: false, candidateId: null});
              }
            }
          }}
          secondaryActions={[
            {
            content: 'Cancel',
            onAction: () => setCandidateDeletionModalState({ open: false, candidateId: null})
            }
          ]}
        loading={false}>
          <Modal.Section>
            Deleting this candidate will remove their data from our system and your list of candidates.
          </Modal.Section>
        </Modal>
        <Modal
          title={`Add tags to ${selectedRows.length} candidate(s)`}
          open={!!isTagModalOpen}
          onClose={() => toggleTagModal()}
          primaryAction={{
            content: 'Update tags',
            onAction: () => handleUpdateCandidatesTags(),
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => toggleTagModal(),
            },
          ]}
          loading={false}>
          <Modal.Section>
            <p>Use descriptive keywords to help organize candidates.</p>
            <br />
            <Autocomplete
              label={'Tags'}
              options={tagOptions}
              selected={selectedBulkTags}
              textField={<Autocomplete.TextField onChange={updateText} value={inputValue} />}
              onSelect={(value) => {
                const selected = [...selectedBulkTags, ...value];
                setSelectedBulkTags(selected.filter((el, index) => selected.indexOf(el) === index));
              }}
              emptyState={
                <Button
                  plain
                  monochrome
                  icon={CirclePlusMinor}
                  fullWidth
                  textAlign={'left'}
                  onClick={handleNewTag}>{`Add ${inputValue}`}</Button>
              }
            />
          </Modal.Section>
          <Modal.Section>
            <Subheading>Applied tags</Subheading>
            <br />
            <Stack spacing={'tight'}>{tagsMarkup}</Stack>
          </Modal.Section>
          <Modal.Section>
            <Subheading>All tags</Subheading>
            <br />
            <Stack>
              {allTagOptions.map(({ label }, index) =>
                selectedBulkTags.includes(label) ? (
                  <TextStyle variation={'subdued'} key={`tag_${index}`}>
                    {label}
                  </TextStyle>
                ) : (
                  <Tag key={`tag_${index}`} onClick={() => setSelectedBulkTags([...selectedBulkTags, label])}>
                    {label}
                  </Tag>
                )
              )}
            </Stack>
          </Modal.Section>
        </Modal>
        <Modal
          open={!!isEmailModalOpen}
          onClose={toggleEmailModal}
          title={`Contact ${selectedRows.length} candidate(s)`}
          primaryAction={{
            content: 'Send',
            onAction: () => handleCandidateBulkEmail(),
            disabled: !(!!emailForm?.from && !!emailForm?.subject && !!emailForm?.message),
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: toggleEmailModal,
            },
          ]}
          loading={false}>
          <Modal.Section>
            <FormLayout>
              <Select
                label='Email template'
                options={externalTemplates.map((template) => {
                  const { id, title } = { ...template };
                  return {
                    value: id,
                    label: title,
                  };
                })}
                value={emailForm?.id}
                onChange={onChangeEmailTemplate}
                placeholder={'Select email template'}
              />
              <FormLayout.Group>
                <TextField
                  label='To'
                  value={toCandidatesTextLabel()}
                  disabled={true}
                  onChange={(to) => setEmailForm({ ...emailForm, to })}
                />
                <Select
                  label='From'
                  options={hiringTeams.map((member) => {
                    const { firstName, lastName, email } = { ...member };
                    return {
                      value: email,
                      label: `${firstName} ${lastName} <${email}>`,
                    };
                  })}
                  onChange={(from) => setEmailForm({ ...emailForm, from })}
                  value={emailForm?.from}
                  placeholder={'Select a hiring team member'}
                />
              </FormLayout.Group>
              <TextField
                label='Subject'
                value={emailForm?.subject}
                onChange={(subject) => setEmailForm({ ...emailForm, subject })}
              />
              <Editor
                value={emailForm?.message}
                initialValue={emailForm?.message || ''}
                init={{
                  height: 310,
                  menubar: false,
                  plugins: ['advlist autolink lists link', 'code fullscreen', 'wordcount'],
                  toolbar:
                    'undo redo | formatselect | fontselect | fontsizeselect | bold italic forecolor | \
                      alignleft aligncenter alignright alignjustify | \
                      bullist numlist outdent indent | removeformat | help',
                }}
                id='body'
                name='body'
                apiKey='czdl8uo4qtlzlp7ozdpws89rify9vokuyuvw21w1jr8n394z'
                onEditorChange={(message) => setEmailForm({ ...emailForm, message })}
              />
            </FormLayout>
          </Modal.Section>
        </Modal>
        <Modal open={isCandidateModalOpen} onClose={toggleIsCandidateModalOpen}>
          <Form
            onSubmit={async (e) => {
              e.preventDefault();
              setLoading(true);
              await createCandidate({
                variables: {
                  name: form.name,
                  lastName: form.lastName,
                  email: form.email,
                  phone: form.phone,
                  shopifyToken,
                  resume: file,
                  jobId: form.jobId,
                },
              });
              setLoading(false);
              toggleIsCandidateModalOpen();
              refetch();
              refetchSubsc();
            }}>
            <Card>
              <Card.Header title='Create Candidate'></Card.Header>
              <Card.Section>
                <FormLayout>
                  <Select id='jobId' label='Job' options={jobsOptions} onChange={updateField('jobId')} value={form.jobId} />
                  <TextField id='name' label='First name' value={form.name} name='name' onChange={updateField('name')} />
                  <TextField
                    id='lastName'
                    label='Last name'
                    value={form.lastName}
                    name='lastName'
                    onChange={updateField('lastName')}
                  />
                  <TextField
                    id='email'
                    label='Email'
                    value={form.email}
                    name='email'
                    type='email'
                    onChange={updateField('email')}
                  />
                  <TextField id='phone' label='Phone' value={form.phone} name='phone' onChange={updateField('phone')} />
                  <DropZone allowMultiple={false} onDrop={handleDropZoneDrop} label='Resume' id='resume'>
                    {uploadedFile}
                    {fileUpload}
                  </DropZone>
                </FormLayout>
              </Card.Section>
            </Card>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                borderTop: '1px solid #dfe3e8',
                marginTop: '20px',
                padding: '20px',
              }}>
              <ButtonGroup>
                <Button onClick={toggleIsCandidateModalOpen} size='medium'>
                  Cancel
                </Button>
                <Button primary={true} submit={true} size='medium' disabled={!form.name || !form.email || !form.phone || !file}>
                  Save
                </Button>
              </ButtonGroup>
            </div>
          </Form>
        </Modal>
        {toastStatus.status && (
          <Toast content={toastStatus.message} onDismiss={() => setToastStatus({ status: false, message: '' })} />
        )}
        {isLoading && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100vh',
              zIndex: '520',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              position: 'absolute',
              top: 0,
              left: 0,
            }}>
            <Loader type='TailSpin' color='#6371c7' height={80} width={80} />
          </div>
        )}
      </Frame>
    </AppProvider>
  );

  function disambiguateLabel(key, values) {
    const jobsTitles = (values || []).map((name = '') => `${name}`.charAt(0).toUpperCase() + `${name}`.slice(1));
    switch (key) {
      case 'job':
        return `Filter by ${jobsTitles.join(', ')}`;
      case 'location':
        return `Hiring location in ${(values || []).join(', ')}`;
      case 'rating':
        return `Rated as ${(values || [])
          .map((rate) => ({ ...overallRatingButtons.find(({ name }) => rate === getOverallRatingKey(name)) }.name))
          .join(', ')}`;
      case 'tag':
        return `Tagged with ${(values || []).join(', ')}`;
      case 'department':
        const departmentsFilter = values
          .map((el) => departments.find(({ id }) => id === el) || {})
          .map(({ headingText }) => headingText);
        return `Filter by ${departmentsFilter.join(', ')}`;
      case 'disposition-code':
        return `Disqualified by ${(values || [])
          .map((code) => ({ ...dispositionCodes.find(({ id }) => code === id) }.name))
          .join(', ')}`;
      case 'apply-date':
        return `Applied the ${(values || [])
          .map((option) => ({ ...appliedDateOptions.find(({ value }) => option === value) }.label))
          .join(', ')
          .toLowerCase()}`;
      case 'score':
        const scoresFilter = values.map((el) => scoreOptions.find(({ value }) => value === el) || {}).map(({ label }) => label);
        return `Scored with ${scoresFilter.join(', ')}`;
      default:
        return jobsTitles;
    }
  }
};

export default CandidateList;
